'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import Small from 'UIComponents/elements/Small';
import H7 from 'UIComponents/elements/headings/H7';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UIButton from 'UIComponents/button/UIButton';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UILink from 'UIComponents/link/UILink';
import IntegrationFeature from 'ecosystem-marketplace-core/records/IntegrationFeature';
import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import * as RequestState from 'ecosystem-marketplace-core/constants/RequestState';
import * as Tier from 'ecosystem-marketplace-core/constants/groupTags/Tier';
import * as TierGroups from 'ecosystem-marketplace-core/constants/groupTags/TierGroup';
import { getTierI18nKey, getTierHubI18nKey, getTierLevelI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import { getPricingUrl } from '../../../../utils/urls';
import { sortTiers } from '../../../../utils/compatibility';
import CompatibilityIcon from '../../../common/CompatibilityIcon';
import { makeGetRequiredPlansForFeatures } from '../../../../selectors/details';
import { getFeatureCompatibilityRequestState } from '../../../../selectors/requestStates';
import * as CustomPropTypes from '../../../../constants/PropTypes';

const getI18nKey = key => `appMarketplace.detail.overview.sidebar.requirements.${key}`;

const CompatibilityDisclaimerHelpIcon = ({
  children,
  hubInstallable
}) => {
  if (!hubInstallable) {
    return children;
  }

  return /*#__PURE__*/_jsx(UIHelpIcon, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: getI18nKey('compatibilityDisclaimer')
    }),
    children: children
  });
};

const PlanCompatibility = ({
  integrationFeatures,
  installability,
  isCustomer,
  isOpsHubListing,
  onOpenFeatureCompatibilityModal,
  tiers,
  trackInteraction
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const pricingPageUrl = useMemo(() => getPricingUrl(isCustomer), [isCustomer]);
  const {
    hubInstallable,
    nonTrialHubInstallable
  } = installability || {};
  const renderedTierGroups = useMemo(() => {
    if (tiers.includes(Tier.HUBSPOT_FREE)) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('allPlans')
      });
    }

    return Object.values(TierGroups).reduce((final, tierGroup) => {
      const includedTiers = tierGroup.filter(tier => tiers.includes(tier));

      if (!includedTiers.length) {
        return final;
      }

      const tier = includedTiers[0];
      const tierI18nKey = getTierI18nKey(tier);

      const renderedTierLabel = /*#__PURE__*/_jsx(FormattedMessage, {
        message: tier === includedTiers[includedTiers.length - 1] ? tierI18nKey : getI18nKey('lowestTier'),
        options: {
          tier: unescapedText(tierI18nKey)
        }
      }, tier);

      return final.concat(renderedTierLabel);
    }, []);
  }, [tiers]);
  const sortedTierGroups = useMemo(() => tiers.sort(sortTiers).reduce((groups, tier) => {
    const [tierHub, tierLevel] = tier.split('_');

    if (!groups.hasOwnProperty(tierHub)) {
      return Object.assign({}, groups, {
        [tierHub]: [tierLevel]
      });
    } else {
      return Object.assign({}, groups, {
        [tierHub]: groups[tierHub].concat(tierLevel)
      });
    }
  }, {}), [tiers]);
  const popoverContent = useMemo(() => {
    return {
      header: /*#__PURE__*/_jsx(H7, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey('compatibilityPopover.heading')
        })
      }),
      body: Object.keys(sortedTierGroups).map(tierHub => /*#__PURE__*/_jsx("div", {
        className: "m-bottom-2",
        children: /*#__PURE__*/_jsxs(Small, {
          children: [/*#__PURE__*/_jsx("div", {
            style: {
              fontWeight: 600
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getTierHubI18nKey(tierHub)
            })
          }), /*#__PURE__*/_jsx(FormattedList, {
            exclusive: true,
            children: sortedTierGroups[tierHub].filter(tierLevel => isValidI18nKey(getTierLevelI18nKey(tierLevel))).map(tierLevel => /*#__PURE__*/_jsx(FormattedMessage, {
              message: getTierLevelI18nKey(tierLevel)
            }, tierLevel))
          })]
        })
      }, tierHub)),
      footer: /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: pricingPageUrl,
          onClick: () => trackInteraction('click tiers tooltip pricing link'),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getI18nKey('compatibilityPopover.seeAllPlans')
          })
        })
      })
    };
  }, [sortedTierGroups, pricingPageUrl, trackInteraction]);
  const getRequiredPlans = useMemo(makeGetRequiredPlansForFeatures, []);
  const requiredPlans = useSelector(state => getRequiredPlans(state, integrationFeatures));
  const featureCompatibilityStatus = useSelector(getFeatureCompatibilityRequestState);
  const partialCompatibilityI18nKey = useMemo(() => {
    if (!isOpsHubListing || !requiredPlans) {
      return '';
    }

    const missingRequiredPlans = requiredPlans.filter(plan => !!plan);

    if (missingRequiredPlans.size === 0) {
      return '';
    } else if (missingRequiredPlans.size <= integrationFeatures.size / 2) {
      return 'most_';
    } else {
      return 'some_';
    }
  }, [requiredPlans, isOpsHubListing, integrationFeatures]);
  const compatibilityI18nKey = useMemo(() => {
    if (!hubInstallable) {
      return 'none';
    } else {
      if (!nonTrialHubInstallable) {
        return `${partialCompatibilityI18nKey}trial`;
      } else {
        return `${partialCompatibilityI18nKey}plan`;
      }
    }
  }, [hubInstallable, nonTrialHubInstallable, partialCompatibilityI18nKey]);

  if (hubInstallable === null) {
    return /*#__PURE__*/_jsx(UILink, {
      external: true,
      href: "https://www.hubspot.com/pricing",
      onClick: () => trackInteraction('click public pricing link'),
      children: /*#__PURE__*/_jsx(FormattedList, {
        exclusive: true,
        children: renderedTierGroups
      })
    });
  }

  const opsHubLoadingStatus = isOpsHubListing ? featureCompatibilityStatus : RequestState.SUCCEEDED;
  const pageLoadingStatus = !installability ? RequestState.PENDING : opsHubLoadingStatus;
  return /*#__PURE__*/_jsxs(LoadingWrapper, {
    requestStatus: pageLoadingStatus,
    children: [/*#__PURE__*/_jsx(UIMediaObject, {
      align: "start",
      itemLeft: /*#__PURE__*/_jsx(CompatibilityIcon, {
        compatible: hubInstallable,
        partial: !!partialCompatibilityI18nKey
      }),
      spacing: "extra-small",
      children: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(CompatibilityDisclaimerHelpIcon, {
          hubInstallable: hubInstallable,
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: getI18nKey(`compatibility.${compatibilityI18nKey}_jsx`),
            options: {
              buttonProps: {
                use: 'link',
                onClick: () => {
                  onOpenFeatureCompatibilityModal();
                  trackInteraction('click partial plan compatibility');
                }
              },
              linkProps: {
                external: true,
                href: pricingPageUrl,
                onClick: () => trackInteraction('click account plan link')
              }
            },
            elements: {
              Button: UIButton,
              Link: UILink
            }
          })
        })
      })
    }), !isOpsHubListing && /*#__PURE__*/_jsx(UIPopover, {
      content: popoverContent,
      open: popoverOpen,
      onOpenChange: evt => setPopoverOpen(evt.target.value),
      showCloseButton: true,
      width: 250,
      children: /*#__PURE__*/_jsx(UIButton, {
        use: "link",
        onClick: () => {
          setPopoverOpen(true);
          trackInteraction('click see all compatible plans popover');
        },
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getI18nKey('compatibilityPopover.button')
          })
        })
      })
    })]
  });
};

PlanCompatibility.propTypes = {
  integrationFeatures: ImmutablePropTypes.listOf(PropTypes.instanceOf(IntegrationFeature)),
  installability: CustomPropTypes.installability,
  isCustomer: PropTypes.bool.isRequired,
  isOpsHubListing: PropTypes.bool,
  onOpenFeatureCompatibilityModal: PropTypes.func.isRequired,
  tiers: ImmutablePropTypes.set.isRequired,
  trackInteraction: PropTypes.func.isRequired
};
export default PlanCompatibility;